import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Table,
  Badge,
  Button,
  Input,
  Alert,
  Spinner,
} from "reactstrap";
import moment from "moment";
import "moment/locale/fr";
import { apiFetch, API } from "../../services/api";

// Les imports produits (chaussures + flux marchands) ne sont pas déclinés par
// marché fr/es : c'est un pilotage d'infra, pas du contenu éditorial. On
// passe donc market: false pour ne pas polluer l'appel avec un ?lang= que
// l'API ne connaît pas.
function getImporters() {
  return apiFetch(`${API.products}/importers`, { market: false }).then((res) =>
    res.json()
  );
}

// Le lancement est TOUJOURS forcé : sans ça, l'importer compare son hash et
// ressort aussitôt en « flux inchangé » sans rien faire, ce qui rend le bouton
// trompeur (un « succès » en quelques secondes, aucun produit retraité).
// Forcer supprime l'entrée de hash côté back, donc le flux est intégralement
// retraité. C'est plus coûteux, d'où le choix de ne pas en faire une action
// répétée : décision produit du 21/08/2026.
function runImporter(key, force) {
  return apiFetch(`${API.products}/importer-run`, {
    method: "POST",
    market: false,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key, force }),
  });
}

// Le back renvoie l'année 1 (0001-01-01T00:00:00Z, le zéro-valeur Go) quand
// l'importer n'a jamais tourné, plutôt qu'une chaîne vide : on l'affiche
// comme une absence de date, pas comme une vraie date de l'an 1.
function formatLastUpdatedAt(iso) {
  if (!iso) return "—";
  const date = moment(iso);
  if (!date.isValid() || date.year() <= 1) return "—";
  return date.format("DD/MM/YYYY HH:mm");
}

// status vide = jamais déclenché manuellement depuis le back-office (mais a
// pu tourner via cron : lastUpdatedAt peut quand même être renseignée).
const STATUS_CONFIG = {
  running: { color: "info", label: "En cours" },
  succeeded: { color: "success", label: "Succès" },
  failed: { color: "danger", label: "Échec" },
  timeout: { color: "warning", label: "Timeout" },
  "": { color: "secondary", label: "Non déclenché" },
};

// Durée à partir de laquelle un run "running" est considéré périmé (runner
// mort avant d'avoir pu réécrire l'item). DOIT rester synchronisée avec
// model.RunStaleAfter côté back (common/domain/model/import_run.go, 20 min) :
// c'est cette même valeur que le back utilise pour ré-autoriser un
// déclenchement. Sans cette vérification côté écran, un runner mort laisse le
// bouton désactivé et la ligne affichée « En cours… » jusqu'au TTL de l'item
// (90 jours), alors que le back accepterait déjà un nouveau lancement.
const RUN_STALE_AFTER_MS = 20 * 60 * 1000;

function isRunStale(importer, now) {
  if (importer.status !== "running") return false;
  const startedAt = moment(importer.startedAt);
  if (!startedAt.isValid()) return false;
  return now - startedAt.valueOf() >= RUN_STALE_AFTER_MS;
}

function StatusBadge({ status, error, stale }) {
  // Un run "running" périmé ne doit pas s'afficher comme un run sain : le
  // point d'interrogation signale que son issue réelle est inconnue (runner
  // probablement mort) plutôt que de laisser croire qu'il tourne toujours.
  const config = stale
    ? { color: "warning", label: "En cours ?" }
    : STATUS_CONFIG[status] || { color: "secondary", label: status };
  // Le message d'erreur peut être long (stack, réponse brute d'un flux) : on
  // le pousse en title (tooltip natif) plutôt que de casser la mise en page
  // du tableau.
  const title = stale
    ? "Aucune mise à jour depuis plus de 20 minutes : le runner a probablement été interrompu. Un nouveau lancement est possible."
    : error || undefined;
  return (
    <Badge color={config.color} title={title} style={title ? { cursor: "help" } : undefined}>
      {config.label}
    </Badge>
  );
}

function ImportersTable({ title, rows, now, launchingByKey, onLaunch }) {
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle tag="h5" className="mb-0">
          {title} <span className="text-muted">({rows.length})</span>
        </CardTitle>
      </CardHeader>
      <CardBody>
        {rows.length === 0 ? (
          <p className="text-muted mb-0">Aucun importer ne correspond à la recherche.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table size="sm" responsive>
              <thead>
                <tr>
                  <th>Importer</th>
                  <th>Dernière MAJ</th>
                  <th>État</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((importer) => {
                  const stale = isRunStale(importer, now);
                  // Un run périmé ne bloque plus rien côté écran, exactement comme
                  // il ne bloque déjà plus un nouveau déclenchement côté back
                  // (model.ImportRun.IsBlocking).
                  const isRunning = importer.status === "running" && !stale;
                  const isLaunching = !!launchingByKey[importer.key];
                  return (
                    <tr key={importer.key}>
                      <td>{importer.label}</td>
                      <td>{formatLastUpdatedAt(importer.lastUpdatedAt)}</td>
                      <td>
                        <StatusBadge status={importer.status} error={importer.error} stale={stale} />
                      </td>
                      <td className="text-right">
                        <Button
                          size="sm"
                          color="primary"
                          disabled={isRunning || isLaunching}
                          onClick={() => onLaunch(importer)}
                        >
                          {isLaunching ? <Spinner size="sm" /> : isRunning ? "En cours…" : "Lancer"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Importers() {
  const [importers, setImporters] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [launchingByKey, setLaunchingByKey] = useState({});
  const [alert, setAlert] = useState(null); // { color, text }
  const [now, setNow] = useState(() => Date.now());

  const fetchImporters = useCallback(() => {
    return getImporters()
      .then((data) => {
        setImporters(Array.isArray(data) ? data : []);
        setLoadError(null);
      })
      .catch(() => {
        setLoadError("Impossible de récupérer la liste des importers.");
      });
  }, []);

  useEffect(() => {
    fetchImporters();
  }, [fetchImporters]);

  // Poll toutes les 30s tant qu'au moins un importer est "en cours" côté back
  // (déclenché par ce back-office ou par un autre), EN EXCLUANT les runs
  // périmés (isRunStale) : un runner mort ne repassera jamais par lui-même en
  // "failed" côté back, donc interroger l'API toutes les 30s pour cette ligne
  // ne ferait que tourner à vide jusqu'au TTL de 90 jours. Dès que plus aucune
  // ligne n'est réellement en cours, l'effet ne reprogramme pas de nouveau
  // tick.
  useEffect(() => {
    const hasRunning = (importers || []).some((i) => !isRunStale(i, Date.now()) && i.status === "running");
    if (!hasRunning) return undefined;
    const id = setInterval(fetchImporters, 30000);
    return () => clearInterval(id);
  }, [importers, fetchImporters]);

  // Fait avancer l'horloge de péremption indépendamment du poll réseau : sans
  // ça, une ligne "en cours" ne basculerait visuellement en "En cours ?"
  // qu'au prochain re-render déclenché par autre chose (recherche, poll...),
  // potentiellement bien après les 20 minutes réelles.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const showAlert = (color, text) => {
    setAlert({ color, text });
  };

  const handleLaunch = (importer) => {
    const { key, label } = importer;
    setLaunchingByKey((prev) => ({ ...prev, [key]: true }));
    setAlert(null);
    runImporter(key, true)
      .then((res) => {
        if (res.status === 202) {
          // On passe la ligne en "en cours" tout de suite, sans attendre le
          // prochain poll : le bouton se désactive dès le clic. startedAt DOIT
          // être remis à maintenant : sans ça, un importer dont le run
          // précédent avait démarré il y a plus de 20 min s'afficherait
          // périmé (isRunStale) dès ce nouveau lancement.
          setImporters((prev) =>
            (prev || []).map((i) =>
              i.key === key ? { ...i, status: "running", error: "", startedAt: new Date().toISOString() } : i
            )
          );
          showAlert("success", `Import « ${label} » lancé.`);
        } else if (res.status === 409) {
          showAlert("warning", `Un import est déjà en cours pour « ${label} ».`);
        } else if (res.status === 400) {
          showAlert("danger", "Importer inconnu ou requête invalide.");
        } else if (res.status === 401) {
          showAlert("danger", "Authentification requise : reconnectez-vous.");
        } else {
          showAlert("danger", `Erreur inattendue (HTTP ${res.status}).`);
        }
      })
      .catch(() => {
        showAlert("danger", "Impossible de contacter le service d'imports.");
      })
      .finally(() => {
        setLaunchingByKey((prev) => ({ ...prev, [key]: false }));
      });
  };

  const filtered = useMemo(() => {
    if (!importers) return [];
    const needle = search.trim().toLowerCase();
    const list = needle
      ? importers.filter((i) => i.label.toLowerCase().includes(needle))
      : importers;
    return [...list].sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [importers, search]);

  const shoesImporters = filtered.filter((i) => i.family === "shoes");
  const feedImporters = filtered.filter((i) => i.family === "feed");
  // Filet de sécurité si une future famille apparaît côté back sans que
  // cet écran soit mis à jour : mieux vaut l'afficher à part que la perdre.
  const otherImporters = filtered.filter((i) => i.family !== "shoes" && i.family !== "feed");

  return (
    <Container fluid className="p-0">
      <Row className="mb-2 mb-xl-4">
        <Col>
          <h3>Imports produits</h3>
          <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
            Lancer un import retraite l'intégralité du flux, sans tenir compte du
            garde-fou qui l'empêche normalement de tourner à vide. À n'utiliser
            que ponctuellement.
          </p>
        </Col>
      </Row>

      {alert ? (
        <Alert color={alert.color} toggle={() => setAlert(null)}>
          {alert.text}
        </Alert>
      ) : null}

      {loadError ? <Alert color="danger">{loadError}</Alert> : null}

      <Row className="mb-3">
        <Col md={4}>
          <Input
            type="text"
            placeholder="Rechercher un importer par nom…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
      </Row>

      {importers === null ? (
        <Row>
          <Col className="d-flex justify-content-center py-5">
            <Spinner color="primary" />
            <span className="text-muted ml-2">Chargement des importers…</span>
          </Col>
        </Row>
      ) : (
        <div>
          <ImportersTable
            title="Marques (chaussures)"
            rows={shoesImporters}
            now={now}
            launchingByKey={launchingByKey}
            onLaunch={handleLaunch}
          />
          <ImportersTable
            title="Flux marchands"
            rows={feedImporters}
            now={now}
            launchingByKey={launchingByKey}
            onLaunch={handleLaunch}
          />
          {otherImporters.length > 0 ? (
            <ImportersTable
              title="Autres"
              rows={otherImporters}
              now={now}
              launchingByKey={launchingByKey}
              onLaunch={handleLaunch}
            />
          ) : null}
        </div>
      )}
    </Container>
  );
}

export default Importers;
